/**
 * Capture — documentation is a first-class feature (TECH_PLAN §0). Grants consume
 * documentation, not builds. These helpers export the drift map and rhizome as
 * PNGs and the raw session as JSON (replayable).
 */

/** Serialize an <svg> element and rasterize it to a downloadable PNG. */
export async function downloadSvgAsPng(svg: SVGSVGElement, filename: string, scale = 2): Promise<void> {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  const bbox = svg.getBoundingClientRect();
  const w = svg.viewBox.baseVal.width || bbox.width || 800;
  const h = svg.viewBox.baseVal.height || bbox.height || 600;
  clone.setAttribute('width', String(w));
  clone.setAttribute('height', String(h));
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

  const data = new XMLSerializer().serializeToString(clone);
  const blob = new Blob([data], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('svg render failed'));
    img.src = url;
  });

  const canvas = document.createElement('canvas');
  canvas.width = w * scale;
  canvas.height = h * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    URL.revokeObjectURL(url);
    return;
  }
  ctx.fillStyle = '#05060a';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  URL.revokeObjectURL(url);

  canvas.toBlob((png) => {
    if (!png) return;
    triggerDownload(URL.createObjectURL(png), filename);
  }, 'image/png');
}

export function downloadJson(obj: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
  triggerDownload(URL.createObjectURL(blob), filename);
}

function triggerDownload(url: string, filename: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/**
 * Optional screen recording of the AR field for documentation, via getDisplayMedia
 * + MediaRecorder. Returns a stop() that resolves with a downloadable webm blob.
 * Not supported on iOS Safari; callers should feature-detect.
 */
export async function startScreenRecording(): Promise<{ stop: () => Promise<Blob> }> {
  const md = navigator.mediaDevices as MediaDevices & {
    getDisplayMedia?: (c?: DisplayMediaStreamOptions) => Promise<MediaStream>;
  };
  if (!md.getDisplayMedia) throw new Error('screen recording unsupported');
  const stream = await md.getDisplayMedia({ video: true });
  const chunks: BlobPart[] = [];
  const rec = new MediaRecorder(stream, { mimeType: 'video/webm' });
  rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
  rec.start();
  return {
    stop: () =>
      new Promise<Blob>((resolve) => {
        rec.onstop = () => {
          stream.getTracks().forEach((t) => t.stop());
          resolve(new Blob(chunks, { type: 'video/webm' }));
        };
        rec.stop();
      }),
  };
}
